"use client";

import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";

import cn from "classnames";

import { GetProductQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { ProductImagesVideo } from "./productImagesVideo.component";

export const ProductImagesMobileThumbnails = ({
  product,
  setSelectedImage,
}: ProductImagesMobileThumbnailsProps) => {
  const containerRef = useRef<HTMLUListElement>(null);
  const [scrollPosition, setScrollPosition] = useState(100);
  useEffect(() => {
    if (!product?.images?.length || !containerRef.current) return;
    // Track the scroll event to update the scroll position
    const handleScroll = () => {
      // calculate if we've reached the end of the scroll
      const maxScrollLeft =
        containerRef.current!.scrollWidth - containerRef.current!.clientWidth;
      const remainingScroll = maxScrollLeft - containerRef.current!.scrollLeft;
      // Get remaining scroll percentage
      const remainingScrollPercentage = (remainingScroll / maxScrollLeft) * 100;
      setScrollPosition(remainingScrollPercentage);
    };
    containerRef.current.addEventListener("scroll", handleScroll);
    return () => {
      containerRef.current?.removeEventListener("scroll", handleScroll);
    };
  }, [product]);
  const renderThumbnails = () => {
    if (!product?.images?.length) return null;
    return product.images.map((productImage, index: number) => {
      if (!productImage?.image) return null;
      // Generate the image URL with specified width and height
      const url = urlFor(productImage.image).width(170).height(170).url();
      if (!url) return null;
      const videoUrl = productImage?.video?.asset?.url;
      return (
        <li
          data-testid="product-images-mobile-thumbnails-list-item"
          className={cn("h-24", {
            "w-42.5 min-w-42.5": videoUrl,
            "w-24 min-w-24": !videoUrl,
          })}
          key={index}>
          <ProductImagesVideo
            imageUrl={url}
            alt={productImage.alt}
            videoUrl={videoUrl}
            index={0}
            onClick={() =>
              setSelectedImage({
                url: urlFor(productImage.image).width(768).height(768).url(),
                alt: productImage.alt,
                videoUrl,
              })
            }
          />
        </li>
      );
    });
  };
  return (
    <div
      data-testid="product-images-mobile-thumbnails"
      className="flex flex-col gap-6">
      <div className="flex justify-center">
        <div className="bg-greyscale-strength relative h-px w-37.5">
          <span
            className={cn(
              "bg-secondary absolute -top-px h-0.75 w-4 rounded-4xl",
            )}
            style={{ right: `${scrollPosition}%` }}></span>
        </div>
      </div>
      <ul
        ref={containerRef}
        data-testid="product-images-mobile-thumbnails-list"
        className="hide-scrollbar flex gap-4 overflow-x-auto">
        {renderThumbnails()}
      </ul>
    </div>
  );
};

export type ProductImagesMobileThumbnailsProps = {
  product: GetProductQueryResult;
  setSelectedImage: Dispatch<
    SetStateAction<{
      url: string;
      alt?: string | null;
      videoUrl?: string;
    } | null>
  >;
};
